import { useEffect, useState } from 'react';

import { useDossier } from '@/features/document-dossier/use-dossier';
import { useActiveCompany } from '@/lib/active-company-context';

import { isCreatingInLms } from './lms-sync-presentation';

// Espera de la creación en el LMS tras reservar (recorrido 2026-11-04): con plantilla, la
// ficha se abre recién cuando el LMS confirma el vínculo, para no mostrar «Sin vincular»
// mientras se crea. Nunca atrapa al usuario: si la ficha no se puede leer o el LMS tarda
// más del tope, termina igual y la ficha sigue refrescándose sola.

export type CreationWaitState = 'waiting' | 'ready' | 'failed' | 'timeout';

export interface CreationWait {
  state: CreationWaitState;
  /** El tipo tiene plantilla: hubo (o hay) un envío de creación al LMS. */
  fromTemplate: boolean;
}

/** Tope de espera: el envío sale en el acto; el ciclo de respaldo corre cada minuto. */
const CREATION_TIMEOUT_MS = 30_000;

export function useLmsCreationWait(
  entryId: string | null,
  { timeoutMs = CREATION_TIMEOUT_MS }: { timeoutMs?: number } = {},
): CreationWait | null {
  const { activeCompanyId } = useActiveCompany();
  const dossier = useDossier(entryId ?? '', {
    companyId: activeCompanyId,
    enabled: entryId !== null,
  });
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setTimedOut(false);
    if (entryId === null) {
      return undefined;
    }
    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [entryId, timeoutMs]);

  if (entryId === null) {
    return null;
  }
  if (dossier.isError) {
    return { state: 'ready', fromTemplate: false };
  }
  const sync = dossier.data?.lmsSync;
  const fromTemplate = (sync?.crear ?? null) !== null;
  if (sync === undefined || isCreatingInLms(sync)) {
    return { state: timedOut ? 'timeout' : 'waiting', fromTemplate };
  }
  const crear = sync.crear;
  if (crear !== null && crear.status !== 'done') {
    return { state: 'failed', fromTemplate };
  }
  return { state: 'ready', fromTemplate };
}
